import {none, option, Option} from "ts-option";
import AuthenticatedEntity, {AuthenticatedEntityTypeEnum} from "../user/AuthenticatedEntity";
import {CreatePaymentApiDto, PaymentApiDto} from "../../api/model/rest";
import User from "../user/User";
import JsonConverter from "../../api/helper/JsonConverter";
import ActivityEvent from "../event/ActivityEvent";
import {getUser} from "../../store/view/user";
import {Icon} from "../../hiberUiKit/src/atoms/Icon/Icon";
import {IconColor} from "../../hiberUiKit/src/molecules/StreamItem/StreamItemIcon";
import DateUtils from "../../utils/DateUtils";
import Attachment from "../attachment/Attachment";
import PaymentRecommendation from "./PaymentRecommendation";
import moment = require("moment");

const uuidv4 = require("uuid/v4");

export default class Payment extends ActivityEvent {

	type: "PAYMENT" = "PAYMENT";

	constructor(public readonly id: string,
		public readonly creationDateTime: moment.Moment,
		public readonly creator: AuthenticatedEntity,
		public readonly amount: number,
		public readonly datePaid: moment.Moment,
		public readonly paymentMethod: string,
		public readonly reference: Option<string>,
		public readonly paymentMethodDetails: Option<string>) {
		super();
	}

	static fromJson(json: PaymentApiDto): Payment {
		const creationDateTime = json.creationDateTime ? moment(json.creationDateTime) : null;
		const creator = AuthenticatedEntity.fromJson(json.creator);
		const datePaid = json.datePaid ? moment(json.datePaid) : null;
		return new Payment(json.id, creationDateTime, creator, json.amount, datePaid, json.paymentMethod,
			option(json.reference), option(json.paymentMethodDetails));
	}

	static create(amount: number, datePaid: moment.Moment, paymentMethod: string, reference: Option<string>) {
		return new Payment(uuidv4(), moment(), null, amount, datePaid, paymentMethod, reference,
			option(`Reference: ${reference}`));
	}

	/**
	 * Creates payments from recommendation - used for displaying recommendation as activity stream item for confirmation purposes.
	 * Creator is null because it don't need to be displayed.
	 * @param {PaymentRecommendation} recommendation
	 * @returns {Payment}
	 */
	static createFromRecommendation(recommendation: PaymentRecommendation) {
		return new Payment(uuidv4(), recommendation.datePaid, null, recommendation.amount, recommendation.datePaid,
			recommendation.paymentMethod, recommendation.reference, recommendation.paymentMethodDetails);
	}

	toJson(): CreatePaymentApiDto {
		const json: CreatePaymentApiDto = {
			amount: this.amount,
			datePaid: this.datePaid.format(),
			paymentMethod: this.paymentMethod,
			reference: this.reference.orNull
		};
		return JsonConverter.removeEmptyFields(json);
	}

	getCssClass() {
		return '';
	}

	getIcon() {
		return Icon.Finance;
	}

	getIconColor() {
		return IconColor.Burgundy;
	}

	getActivityMessage() {
		const message = `paid on ${this.datePaid.format(
			DateUtils.DATE_FORMAT)} via ${PaymentMethod[this.paymentMethod as any]}`;
		return this.paymentMethodDetails.map(details => `${message} (${details})`).getOrElse(message);
	}

	getCreationDate() {
		return this.creationDateTime;
	}

	getAttachments(): Attachment[] {
		return [];
	}

	getActivityType(): string {
		return `INVOICE`;
	}

	getActivityAdditionalMessages(): string[] {
		return [];
	}

	getId() {
		return this.id;
	}

	/**
	 * Get user display name.
	 */
	getUserDisplayName(): string {
		return this.creator.getDisplayName();
	}
}

export enum PaymentMethod {
	CARD = "Card",
	BANK_TRANSFER = "Bank transfer",
	STRIPE_PAYOUT = "Stripe payout",
	GOCARDLESS_PAYOUT = "GoCardless payout",
	DIRECT_DEBIT = "Direct debit"
}

export enum PaymentConfirmationStatus {
	Unconfirmed = "UNCONFIRMED",
	Confirmed = "CONFIRMED",
	Rejected = "REJECTED"
}
